import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ActivityIndicator, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {observer} from 'mobx-react-lite';

import AskToVerify from '../../components/rewards/AskToVerify';
import BackButton from '../../components/rewards/BackButton';
import CheckBox from '../../components/rewards/CheckBox';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import StaffStorePicker from '../../components/StaffStorePicker';
import Button from '../../components/themed/Button';
import DateOfBirthInput from '../../components/themed/DateOfBirthInput';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import SuburbPicker from '../../components/ui-controls/address/SuburbPicker';
import TextInput from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {AppRoute} from '../../models/AppRoute';
import {Shop} from '../../models/middleware';
import {
  AccountScreenAction,
  InformationContentType,
  RouteNavigatorScreenProps,
} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {formatDateForZen} from '../../utils/date';
import {logger, serializeError} from '../../utils/logger';
import {rewardsMarketingName} from '../../utils/rewards';
import {
  emailSchema,
  RewardsSignUpFormData,
  rewardsSignUpSchema,
} from '../account/schemas/authSchemas';

import {styles} from './styles';
import {useRewardsTermsAndConditionsPopper} from './TermsAndConditions';

const RewardsSignUp = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_SIGN_UP>) => {
    const {rewardsStore, offersStore} = useStores();
    const {flowType} = route.params || {};
    const [selectedStaffStore, setSelectedStaffStore] = useState<Shop | null>(null);
    const [staffStoreError, setStaffStoreError] = useState<string | null>(null);
    const [emailValidating, setEmailValidating] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const tabBarPadding = useTabBarBottomPadding();

    // DIS-1660: the sign up code is URL-supplied and validated on load by
    // RewardsSignInScreen. Here it is display-only, and only when it still
    // matches a currently loaded offer — an absent, empty or unread offers
    // list keeps the field hidden rather than showing an unverified code.
    const signUpOffers = offersStore.signUpOfferData;
    const isSignUpCodeValid = Boolean(
      rewardsStore.signUpCode &&
      signUpOffers?.some(offer => offer.signUpCode === rewardsStore.signUpCode),
    );

    const mountedRef = useRef(true);
    const emailValidationReqIdRef = useRef(0);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const handleStaffStoreSelected = useCallback(
      (shop: Shop) => {
        setSelectedStaffStore(shop);
        setStaffStoreError(null);
        rewardsStore.setStaffStoreId(shop.store_number);
        // DIS-1236: persist full Shop alongside id so RewardsVerificationScreen
        // can short-circuit phase 3 directly (no redundant re-pick) and render
        // the "YOUR SELECTED STORE" affordance for SIGNUP_LITE staff.
        rewardsStore.setStaffStore(shop);
      },
      [rewardsStore],
    );

    const {dialogNode, textNode} = useRewardsTermsAndConditionsPopper({
      text: 'Terms and Conditions',
      contentType: InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
    });

    const {
      control,
      handleSubmit,
      formState: {errors, touchedFields, isSubmitting},
      setError,
    } = useForm<RewardsSignUpFormData>({
      resolver: zodResolver(rewardsSignUpSchema),
      defaultValues: {
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: null,
        phoneNumber: rewardsStore.phoneNumber,
        confirmCheckbox: false,
        suburb: null,
        signUpCode: rewardsStore.signUpCode || '',
      },
      mode: 'onBlur',
    });

    const handleEmailBlur = async (formOnBlur: () => void, value: string) => {
      formOnBlur();
      const formatResult = emailSchema.safeParse(value);
      if (!formatResult.success) return;
      const myReqId = ++emailValidationReqIdRef.current;
      setEmailValidating(true);
      try {
        const response = await apiService.validateEmail(value);
        if (!mountedRef.current || myReqId !== emailValidationReqIdRef.current) return;
        if (!response.data.valid) {
          setError('email', {
            type: 'validate',
            message: response.data.message || 'Please enter a valid email address',
          });
        }
      } catch (e) {
        if (!mountedRef.current || myReqId !== emailValidationReqIdRef.current) return;
        logger.debug('SignUp', 'Email domain validation failed', serializeError(e));
        setError('email', {
          type: 'validate',
          message: 'Unable to validate email. Please try again.',
        });
      } finally {
        if (mountedRef.current && myReqId === emailValidationReqIdRef.current) {
          setEmailValidating(false);
        }
      }
    };

    const onSubmit = async (values: RewardsSignUpFormData) => {
      setSubmitError(null);
      // DIS-1135 bug_008: invalidate any in-flight email-blur validation
      // so its late response can't setError('email') after submit has
      // navigated away.
      ++emailValidationReqIdRef.current;
      try {
        // Validate email domain via Zen API before proceeding (mirrors AccountEntryScreen)
        try {
          const validationResponse = await apiService.validateEmail(values.email || '');
          if (!validationResponse.data.valid) {
            setError('email', {
              type: 'validate',
              message: validationResponse.data.message || 'Please enter a valid email address',
            });
            return;
          }
        } catch (e) {
          logger.error('SignUp', 'Email validation failed on submit', serializeError(e));
          setError('email', {
            type: 'validate',
            message: 'Unable to validate email. Please try again.',
          });
          return;
        }

        if (!values.dateOfBirth) {
          setError('dateOfBirth', {type: 'manual', message: 'Date of birth is required'});
          return;
        }

        const zedDOB = formatDateForZen(values.dateOfBirth);

        rewardsStore.setFirstName(values.firstName || '');
        rewardsStore.setLastName(values.lastName || '');
        rewardsStore.setEmailAddress(values.email || '');
        rewardsStore.setDateOfBirth(zedDOB);

        if (!values.suburb) {
          setError('suburb', {type: 'manual', message: 'Suburb is required'});
          return;
        }

        rewardsStore.setSuburb({
          locality: values.suburb.locality || '',
          postal_code: values.suburb.postal_code || '',
          administrative_area: values.suburb.administrative_area || '',
        });

        if (rewardsStore.isStaffSignup && !rewardsStore.staffStoreId) {
          setStaffStoreError('Please select a store');
          return;
        }

        if (!rewardsStore?.phoneNumber) {
          logger.error('SignUp', 'Phone number is required');
        } else {
          const result = await apiService.rewardsVerifyNewUserPhoneNumber({
            phoneNumber: rewardsStore.phoneNumber,
            emailAddress: values.email || '',
          });

          if (result?.data) {
            if (result.data.emailAlreadyExists) {
              setError('email', {
                type: 'manual',
                message:
                  'Email address already in use. Please use a different email address or contact support',
              });
            } else {
              // {"status":"pending"} is the typical response
              navigation.navigate(AppRoute.REWARDS_VERIFICATION, flowType ? {flowType} : undefined);
            }
          }
          // TODO else we have some other error, and need to return a generic error message
        }
      } catch (e) {
        logger.error('SignUp', 'Error in /send-verification-new-lite-user', serializeError(e));
        if (mountedRef.current) {
          setSubmitError('Could not send verification code. Please try again.');
        }
      }
    };

    const renderErrorMessageWhenTouchedOrPlaceholder = (name: keyof RewardsSignUpFormData) => {
      logger.debug('SignUp', 'Rendering error message', {field: name, errors});
      return (
        <Text style={styles.errorMessage} id={`${name}-error`}>
          {touchedFields[name] && errors[name] ? errors[name]?.message : ' '}
        </Text>
      );
    };

    return (
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps="always"
        contentContainerStyle={{paddingBottom: tabBarPadding}}>
        <ContentView>
          <BackButton
            onPress={() => {
              // DIS-1318: preserve staff/signUpCode through back-nav
              navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
            }}
          />
          <RewardsProgramImage variant="onLight" />
          <Text variant="heading1">SIGN UP FOR {rewardsMarketingName.toUpperCase()}</Text>
          <Text>Enter your details below for great savings and member only prices!</Text>
          <View style={{gap: 8, zIndex: 1000, marginTop: 30}}>
            <Text variant="subheading" accessibilityRole="header">
              ABOUT YOU
            </Text>
            <Controller
              control={control}
              name="firstName"
              render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                <TextInput
                  accessibilityLabel="First name"
                  autoCapitalize="words"
                  autoComplete="given-name"
                  errorMessageWhenTouched={error?.message}
                  keyboardType="default"
                  label="First name"
                  maxLength={50}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  touched={isTouched}
                  value={value || ''}
                />
              )}
            />
            <Controller
              control={control}
              name="lastName"
              render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                <TextInput
                  accessibilityLabel="Last name"
                  autoCapitalize="words"
                  autoComplete="family-name"
                  errorMessageWhenTouched={error?.message}
                  keyboardType="default"
                  label="Last name"
                  maxLength={50}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  touched={isTouched}
                  value={value || ''}
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                <TextInput
                  accessibilityLabel="Email address"
                  autoCapitalize="none"
                  autoComplete="email"
                  errorMessageWhenTouched={error?.message}
                  keyboardType="email-address"
                  label="Email address"
                  maxLength={100}
                  onBlur={() => handleEmailBlur(onBlur, value || '')}
                  onChangeText={onChange}
                  touched={isTouched}
                  value={value || ''}
                />
              )}
            />
            {emailValidating ? (
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4}}
                testID="email-validating-indicator">
                <ActivityIndicator size="small" />
                <Text variant="body2" color="textMuted">
                  Validating email…
                </Text>
              </View>
            ) : null}

            <View style={styles.dateOfBirthContainer}>
              <Text variant="body2" style={{marginBottom: 8}}>
                Date of Birth
              </Text>
              <Controller
                control={control}
                name="dateOfBirth"
                render={({field: {onChange, value}}) => (
                  <DateOfBirthInput
                    initialDate={value || undefined}
                    onChange={dateString => {
                      onChange(dateString);
                    }}
                    onInvalidDate={errorMessage => {
                      setError('dateOfBirth', {type: 'manual', message: errorMessage});
                    }}
                  />
                )}
              />
              {renderErrorMessageWhenTouchedOrPlaceholder('dateOfBirth')}
            </View>

            <View style={{zIndex: 2000, elevation: 2}}>
              <Text variant="body2" style={{marginBottom: 8}}>
                Suburb
              </Text>
              <Controller
                control={control}
                name="suburb"
                render={({field: {onChange, value}}) => (
                  <SuburbPicker
                    onSelectSuburb={onChange}
                    initialSuburb={value}
                    testID="suburb-picker"
                  />
                )}
              />
              {renderErrorMessageWhenTouchedOrPlaceholder('suburb')}
            </View>

            <View style={{zIndex: 1000, elevation: 1}}>
              <TextInput
                accessibilityLabel="Mobile number"
                autoCapitalize="none"
                keyboardType="phone-pad"
                label="Mobile number"
                maxLength={20}
                // have to use rewardStore.phoneNumber
                // initialValues.phoneNumber might not be up to date (tldr mobx)
                value={rewardsStore.phoneNumber}
                disabled
              />

              <AskToVerify action={AccountScreenAction.CREATE} />

              {isSignUpCodeValid ? (
                <TextInput
                  accessibilityLabel="Sign up code"
                  label="Sign up code"
                  value={rewardsStore.signUpCode}
                  disabled
                />
              ) : null}
            </View>

            {/* DIS-1384 R5/K1: LMG staff path renders no store field, no
                department/state field, and no PROMO CODE field (heading
                included) on this screen — selection happens on the
                dedicated LmgStaffSelectionScreen after OTP. */}
            {Boolean(!rewardsStore.isLmgStaffFlow && rewardsStore.isStaffSignup) && (
              <>
                <Text variant="subheading" accessibilityRole="header">
                  YOUR STORE
                </Text>
                <View>
                  <StaffStorePicker
                    mode="compact"
                    onStoreSelected={handleStaffStoreSelected}
                    selectedStore={selectedStaffStore}
                    testID="staff-store-picker"
                  />
                  {staffStoreError ? (
                    <Text style={styles.errorMessage}>{staffStoreError}</Text>
                  ) : null}
                </View>
              </>
            )}
          </View>

          <View style={{marginTop: 30}}>
            <Controller
              control={control}
              name="confirmCheckbox"
              render={({field: {onChange, value}}) => (
                <CheckBox
                  text={
                    <>
                      <Text variant="body2">
                        When signing up to the {rewardsMarketingName} program, you agree with the{' '}
                        {textNode}
                      </Text>
                    </>
                  }
                  checked={value}
                  onChange={() => onChange(!value)}
                />
              )}
            />
            {renderErrorMessageWhenTouchedOrPlaceholder('confirmCheckbox')}
          </View>
          {dialogNode}

          <View style={{marginTop: 16}}>
            {submitError ? (
              <Text
                variant="body2"
                color="error"
                style={{marginBottom: 8}}
                accessibilityRole="alert"
                accessibilityLiveRegion="polite"
                testID="signup-submit-error">
                {submitError}
              </Text>
            ) : null}
            <Button
              title={'CONTINUE'}
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsSignUp;
